import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authAwareRefetchInterval } from '@siliconx/shared/query/queryClient';
import { Bell, Check } from 'lucide-react';
import type { NotificationDTO } from '@siliconx/shared';
import { cn } from '@siliconx/shared/ui/utils';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { isNotificationLinkable, relativeTime, renderNotification, unreadBadgeLabel } from '../../lib/notifications';
import { Button } from '@siliconx/shared/ui/Button';

/**
 * Bell icon + unread badge + dropdown feed (SIL-160, layer 5 of the
 * notifications epic SIL-155). Lives in AppShell's header, next to the
 * theme toggle / sign-out controls.
 */

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: unread } = useQuery({
    queryKey: qk.notifications.unreadCount(),
    queryFn: async () => {
      const res = await api.api.client.notifications['unread-count'].$get();
      await throwIfNotOk(res);
      return res.json();
    },
    // SIL-667: this endpoint alone served 11,806 401s over 7 days — a bare
    // 60_000 interval kept firing forever against an expired token. Stops
    // once this query is failing on auth; resumes once it next succeeds.
    refetchInterval: authAwareRefetchInterval(60_000),
  });

  const { data: list, isLoading } = useQuery({
    queryKey: qk.notifications.list(),
    queryFn: async () => {
      const res = await api.api.client.notifications.$get({ query: { limit: '15' } });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      return res.json();
    },
    enabled: open,
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      const res = await api.api.client.notifications['read-all'].$post();
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.notifications.unreadCount() });
      void queryClient.invalidateQueries({ queryKey: qk.notifications.list() });
    },
  });

  const markOneRead = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.api.client.notifications[':id'].read.$post({ param: { id } });
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.notifications.unreadCount() });
      void queryClient.invalidateQueries({ queryKey: qk.notifications.list() });
    },
  });

  // Click-outside + Escape dismissal (mirrors the pattern in
  // apps/operations/src/routes/skus_.build.tsx).
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  function handleRowClick(n: NotificationDTO) {
    if (!n.read) markOneRead.mutate(n.id);
    setOpen(false);
    if (isNotificationLinkable(n.type)) {
      // linkPath is API-supplied and covers many route shapes — navigate
      // imperatively rather than typing every destination as a Link.
      void navigate({ to: n.linkPath });
    }
  }

  const badgeLabel = unreadBadgeLabel(unread?.count ?? 0);

  return (
    <div ref={containerRef} className="relative">
      <Button variant="icon" className="relative"
        aria-label={
          badgeLabel ? `Notifications, ${unread?.count} unread` : 'Notifications, no unread'
        }
        onClick={() => setOpen((v) => !v)}
      >
        <Bell className="size-4" />
        {badgeLabel && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-xs font-medium leading-none text-white">
            {badgeLabel}
          </span>
        )}
      </Button>

      {open && (
        <div
          role="dialog"
          aria-label="Notifications"
          className="absolute right-0 z-50 mt-2 w-80 rounded-lg border border-zinc-200 bg-white shadow-lg"
        >
          <div className="flex items-center justify-between border-b border-zinc-200 px-3 py-2">
            <span className="text-sm font-medium text-zinc-700">Notifications</span>
            <button
              type="button"
              onClick={() => markAllRead.mutate()}
              disabled={markAllRead.isPending || !unread?.count}
              className="flex items-center gap-1 text-xs text-brand-600 hover:underline disabled:cursor-not-allowed disabled:text-zinc-300 disabled:no-underline"
            >
              <Check className="size-3" /> Mark all read
            </button>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {isLoading && <p className="px-3 py-4 text-center text-sm text-zinc-400">Loading…</p>}
            {!isLoading && (list?.data.length ?? 0) === 0 && (
              <p className="px-3 py-6 text-center text-sm text-zinc-400">No notifications yet</p>
            )}
            {list?.data.map((n) => {
              const { title, detail } = renderNotification(n);
              const linkable = isNotificationLinkable(n.type);
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => handleRowClick(n)}
                  disabled={!linkable && n.read}
                  className={cn(
                    'flex w-full flex-col gap-0.5 border-b border-zinc-100 px-3 py-2.5 text-left last:border-b-0 hover:bg-zinc-50',
                    !n.read && 'bg-brand-50/60',
                    !linkable && 'cursor-default hover:bg-transparent',
                  )}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-brand-600" />}
                    <span className={cn('text-sm', n.read ? 'text-zinc-600' : 'font-medium text-zinc-900')}>
                      {title}
                    </span>
                  </div>
                  {detail && <span className="pl-3.5 text-xs text-zinc-400">{detail}</span>}
                  <span className="pl-3.5 text-xs text-zinc-400">{relativeTime(n.createdAt)}</span>
                </button>
              );
            })}
          </div>

          <div className="border-t border-zinc-200 px-3 py-2 text-center">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                void navigate({ to: '/notifications' });
              }}
              className="text-xs text-brand-600 hover:underline"
            >
              See all
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
